// Shared presentation of the autopilot ("Automatisch abarbeiten") on a
// developer to-do, used by the developer task list and by the Dev-Issues board
// so both read the same.
//
// The autopilot itself lives in the backend (`Todo.autoRun`): a human marks the
// card and a scheduled run claims one at a time, implements it, opens a pull
// request and deploys to staging. It never merges; once a human has merged, a
// later run ticks the task off. This module only labels that state.

import dayjs from 'dayjs';

// The label a marked card currently deserves, or null when it is not marked.
// `color` is a MUI Chip colour, so the caller only has to render it.
export const autopilotStatus = autoRun => {
  if (!autoRun || (!autoRun.enabled && !autoRun.state)) return null;

  const marked = autoRun.requestedByUsername
    ? `Markiert von ${autoRun.requestedByUsername}${
        autoRun.requestedAt ? ` am ${dayjs(autoRun.requestedAt).format('DD.MM.YYYY')}` : ''
      }.`
    : 'Für die automatische Abarbeitung markiert.';

  switch (autoRun.state) {
    case 'running':
      return {
        label: 'Autopilot läuft',
        color: 'info',
        hint: `${marked} Ein Lauf arbeitet gerade daran.`,
      };
    case 'delivered':
      return {
        label: 'Autopilot: PR bereit',
        color: 'info',
        hint: `${marked} Der Lauf ist durch: Pull Request steht, Staging ist deployt. Mergen bleibt bei dir — danach hakt der nächste Lauf die Aufgabe ab.`,
      };
    case 'merged':
      return {
        label: 'Autopilot: abgehakt',
        color: 'success',
        hint: `${marked} Der Pull Request ist gemerged und live, die Aufgabe wurde daraufhin abgehakt.`,
      };
    case 'blocked':
      return {
        label: 'Autopilot: Blick nötig',
        color: 'warning',
        hint: `${marked} Der Lauf hat bewusst gestoppt${
          autoRun.note ? `: ${autoRun.note}` : ''
        } — der Pull Request liegt bereit.`,
      };
    case 'failed':
      return {
        label: 'Autopilot abgebrochen',
        color: 'error',
        hint: `${marked} Der Lauf ist abgebrochen${autoRun.note ? `: ${autoRun.note}` : ''}.`,
      };
    case 'queued':
    default:
      return {
        label: 'Autopilot wartet',
        color: 'default',
        hint: `${marked} Der nächste geplante Lauf nimmt sie sich.`,
      };
  }
};

// "Markiert von X am …" — the human approval the whole autopilot stands on.
export const autopilotMarkedBy = autoRun => {
  if (!autoRun?.requestedByUsername) return null;
  return `Markiert von ${autoRun.requestedByUsername}${
    autoRun.requestedAt ? ` am ${dayjs(autoRun.requestedAt).format('DD.MM.YYYY')}` : ''
  }`;
};

// What the toggle promises, in one sentence — identical wording in both views.
export const AUTOPILOT_HINT =
  'Der nächste geplante Lauf holt sich diese Karte, setzt sie um, öffnet einen Pull Request gegen ' +
  'master und deployt auf Staging. Gemerged wird nichts — das bleibt bei dir. Sobald du gemerged ' +
  'hast, hakt ein späterer Lauf die Aufgabe ab. Beides, „In Arbeit" und „Erledigt", ' +
  'benachrichtigt den Melder.';
